import React, { createContext, useContext, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { api } from '@/api/apiClient';
import { toast } from 'react-hot-toast';

const BrandingContext = createContext({ theme: 'modern', colorScheme: 'light', saving: false, disabled: true });
const publicPaths = new Set(['/login', '/register', '/forgot-password', '/set-password', '/verify-email', '/privacy', '/support', '/account-deleted']);

export function BrandingProvider({ children }) {
  const { user, impersonating, setBrandingPreference, setColorSchemePreference } = useAuth();
  const { pathname } = useLocation();
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [colorPreview, setColorPreview] = useState(null);
  const inFlight = useRef(false);
  const userId = user?.id || user?._id;
  const activeUser = useRef(userId);
  activeUser.current = userId;
  const savedTheme = user?.branding_theme === 'legacy' ? 'legacy' : 'modern';
  const theme = publicPaths.has(pathname.replace(/\/$/, '')) || !user
    ? 'modern' : preview?.userId === userId ? preview.theme : savedTheme;
  const disabled = !user || impersonating || saving;
  const colorScheme = colorPreview && colorPreview.userId === userId ? colorPreview.scheme : user?.color_scheme === 'dark' ? 'dark' : 'light';

  useLayoutEffect(() => {
    document.documentElement.dataset.branding = theme;
    document.documentElement.classList.toggle('dark', !!user && !publicPaths.has(pathname.replace(/\/$/, '')) && colorScheme === 'dark');
  }, [theme, colorScheme, userId, pathname]);

  const changeColorScheme = async scheme => {
    if (disabled || inFlight.current || !['light', 'dark'].includes(scheme)) return;
    inFlight.current = true;
    setSaving(true);
    setColorPreview({ userId, scheme });
    try {
      const updated = await api.auth.updateMe({ color_scheme: scheme });
      if (activeUser.current === userId) setColorSchemePreference(userId, updated.color_scheme);
    } catch {
      if (activeUser.current === userId) toast.error('Could not save dark mode. Please try again.');
    } finally { setColorPreview(null); setSaving(false); inFlight.current = false; }
  };

  const changeTheme = async next => {
    if (disabled || inFlight.current || !['modern', 'legacy'].includes(next)) return;
    inFlight.current = true;
    setSaving(true);
    setPreview({ userId, theme: next });
    try {
      const updated = await api.auth.updateMe({ branding_theme: next });
      if (activeUser.current === userId) setBrandingPreference(userId, updated.branding_theme);
    } catch {
      if (activeUser.current === userId) toast.error('Could not save your appearance. Please try again.');
    } finally {
      setPreview(null);
      setSaving(false);
      inFlight.current = false;
    }
  };
  return <BrandingContext.Provider value={{ theme, changeTheme, colorScheme, changeColorScheme, saving, disabled }}>{children}</BrandingContext.Provider>;
}

export const useBranding = () => useContext(BrandingContext);
